import { parseQueryString } from '@zg-rentals/string-utils';

import PropertiesConstants from '../constants/PropertiesConstants';
import QueryConstants from '../constants/QueryConstants';
import PropertiesPageConstants from '../containers/PropertiesPage/PropertiesPageConstants';

import contextUtils from './contextUtils';
import postingUtils from './postingUtils';

import type { History, Location } from 'history';
import type { Paths } from '@zg-rentals/paths-manifest';
import type {
  AccountViewListingsDto,
  RentalManagerUserPropertyListDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { FilterState } from '../containers/ZRAMPropertiesPage';

export const renderInitialDisplayModal = ({
  search,
  paths,
  totalListingsWithoutFilters,
  setShowAddPropertyModal,
  canAccessOnboardingWizard,
  properties,
  showAddPropertyModal,
  setShowAddFeedListingsModal,
  setShowPostClaimingModal,
  setShowClaimPropertiesModal,
  canAccessClaimPathModal,
  canAccessClaimPathMorePropertiesModal,
  setShowSpotlightListingsFirstUseModal,
  canAccessSpotlightListingsFirstUseModal,
  showSpotlightListingsFirstUseModal,
}: {
  search: string;
  paths: Paths;
  totalListingsWithoutFilters: number;
  showAddPropertyModal: boolean;
  setShowAddPropertyModal: ({ shouldSendAnalytics }: { shouldSendAnalytics?: boolean }) => void;
  canAccessOnboardingWizard: boolean;
  properties: AccountViewListingsDto | RentalManagerUserPropertyListDto;
  setShowAddFeedListingsModal: () => void;
  setShowPostClaimingModal?: (isOpen: boolean) => void;
  setShowClaimPropertiesModal?: ({ isOpen, isClaimMoreModal }: { isOpen: boolean; isClaimMoreModal: boolean }) => void;
  canAccessClaimPathModal?: boolean;
  canAccessClaimPathMorePropertiesModal?: boolean;
  setShowSpotlightListingsFirstUseModal?: (isOpen: boolean) => void;
  canAccessSpotlightListingsFirstUseModal?: boolean;
  showSpotlightListingsFirstUseModal?: boolean;
}) => {
  // If spotlight first-use modal is already open (e.g. from late-loading spotlight data), don't open another initial modal
  if (showSpotlightListingsFirstUseModal) {
    return;
  }

  const queryParams = parseQueryString(search);

  const isFromMarketingPaymentsRoute = contextUtils.isFromMarketingPaymentsRoute(search);
  const isFromMarketingApplicationsRoute = contextUtils.isFromMarketingApplicationsRoute(search);
  const isFromMarketingLeasesRoute = contextUtils.isFromMarketingLeasesRoute(search);
  const isFromLandingPage = queryParams[QueryConstants.POSTING_PATH] === 'true';
  const isFromPaymentsAggregateViewPage = contextUtils.isRedirectToPayments(search);
  const isFromClaimDashboard = queryParams[QueryConstants.CLAIM_DASHBOARD] === 'true';
  const foundAddAFeedParam = queryParams[QueryConstants.ADD_A_FEED] === 'true';

  if (isFromMarketingPaymentsRoute || isFromMarketingApplicationsRoute || isFromMarketingLeasesRoute) {
    const marketingRoute = postingUtils.getDestinationTab(paths, search);

    // if no listings and not already onboarding, show add property modal
    if (totalListingsWithoutFilters === 0 && !showAddPropertyModal && !canAccessOnboardingWizard) {
      setShowAddPropertyModal({ shouldSendAnalytics: true });
      return;
    }

    // if 1 listing go to PDP (../../.. or payments)
    if (totalListingsWithoutFilters === 1) {
      const { listingAlias } = properties.listings[0];
      const targetRoute = `${paths.propertiesPage}/${listingAlias}/${marketingRoute}${search}`;

      // nav to pdp
      window.location.href = targetRoute;
      return;
    }
  }

  const isFromSignInLPAndNoListings =
    queryParams[QueryConstants.SIGN_IN_LP] === 'true' && totalListingsWithoutFilters === 0;
  // if coming from zillow landing page or hotpads post a listing link and `AddPropertyModal` and `WizardUserOnboarding` is not already displaying
  if (
    (isFromLandingPage || isFromSignInLPAndNoListings || isFromPaymentsAggregateViewPage) &&
    !showAddPropertyModal &&
    !canAccessOnboardingWizard
  ) {
    // track only if coming from Get Started button on landing page or if routed from Aggregate View page
    setShowAddPropertyModal({ shouldSendAnalytics: isFromLandingPage || isFromPaymentsAggregateViewPage });
    return;
  }

  if (foundAddAFeedParam) {
    setShowAddFeedListingsModal();
    return;
  }

  if (isFromClaimDashboard) {
    setShowPostClaimingModal?.(true);
    return;
  }

  if (canAccessClaimPathModal || canAccessClaimPathMorePropertiesModal) {
    setShowClaimPropertiesModal?.({
      isOpen: true,
      isClaimMoreModal: canAccessClaimPathMorePropertiesModal ?? false,
    });
    return;
  }

  if (canAccessSpotlightListingsFirstUseModal) {
    setShowSpotlightListingsFirstUseModal?.(true);
    return;
  }
};

export const closeBanner = (
  bannerToClose: (typeof PropertiesPageConstants)[keyof typeof PropertiesPageConstants],
  setDisplayPaymentsNotification: (isOpen: boolean) => void,
  setDisplayApplicationsNotification: (isOpen: boolean) => void,
  setDisplayLeasesNotification: (isOpen: boolean) => void,
  setDisplayListingClaimCompleteBanner: (isOpen: boolean) => void,
) => {
  switch (bannerToClose) {
    case PropertiesPageConstants.DISPLAY_PAYMENTS_NOTIFICATION:
      setDisplayPaymentsNotification(false);
      break;
    case PropertiesPageConstants.DISPLAY_APPLICATIONS_NOTIFICATION:
      setDisplayApplicationsNotification(false);
      break;
    case PropertiesPageConstants.DISPLAY_LEASES_NOTIFICATION:
      setDisplayLeasesNotification(false);
      break;
    case PropertiesPageConstants.DISPLAY_LISTING_CLAIM_COMPLETE_BANNER:
      setDisplayListingClaimCompleteBanner(false);
      break;
    default:
      break;
  }
};

export const removeQueryParam = (targetQueryParams: Array<string>, location: Location, history: History) => {
  const queryParams = new URLSearchParams(location.search);

  targetQueryParams.forEach((queryParam) => {
    if (queryParams.has(queryParam)) {
      queryParams.delete(queryParam);

      history.replace({
        ...location,
        search: queryParams.toString(),
      });
    }
  });
};

export const getPhotoUrls = (isRunningInProd: boolean, photoKey: string) => {
  const environmentHost = isRunningInProd ? 'photos.zillowstatic.com' : 'photos.test.zillow.net';

  return {
    medium: `https://${environmentHost}/fp/${photoKey}-rentals_medium_500_500.webp`,
    thumb: `https://${environmentHost}/fp/${photoKey}-rentals_thumb_180_180.webp`,
  };
};

export const updateUrlWithFilters = (filters: FilterState, location: Location, history: History) => {
  const queryParams = new URLSearchParams(location.search);
  let hasChanges = false;

  // Update subNavFilterType
  const currentSubNavFilterType = queryParams.get(QueryConstants.SUB_NAV_FILTER_TYPE);
  const newSubNavFilterType = filters.subNavFilterType;

  if (currentSubNavFilterType !== newSubNavFilterType) {
    queryParams.set(QueryConstants.SUB_NAV_FILTER_TYPE, newSubNavFilterType);
    hasChanges = true;
  }

  // Update searchText
  const currentSearchText = queryParams.get(QueryConstants.SEARCH_TEXT);
  const newSearchText = filters.searchText;

  if (currentSearchText !== newSearchText) {
    if (newSearchText && newSearchText.trim() !== '') {
      queryParams.set(QueryConstants.SEARCH_TEXT, newSearchText);
    } else {
      queryParams.delete(QueryConstants.SEARCH_TEXT);
    }
    hasChanges = true;
  }

  // Only update URL if there are actual changes
  if (hasChanges) {
    history.replace({
      ...location,
      search: queryParams.toString(),
    });
  }
};

export const initializeFiltersFromUrl = (location: Location, defaultFilters: FilterState): FilterState => {
  const queryParams = parseQueryString(location.search);

  const subNavFilterType = queryParams[QueryConstants.SUB_NAV_FILTER_TYPE];
  const searchText = queryParams[QueryConstants.SEARCH_TEXT];

  return {
    ...defaultFilters,
    subNavFilterType: subNavFilterType || defaultFilters.subNavFilterType,
    searchText: searchText || defaultFilters.searchText,
  };
};

export const transformFiltersToApiParams = (filters: FilterState) => ({
  includeActive:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  includeInactive:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  includeArchived: filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
  includeUnarchived: filters.subNavFilterType !== PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
  includeFreeClaimedListingsOnly:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED,
  includeFreeBonusListingsOnly:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS,
  includeAtRiskOnly: filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK,
  searchText: filters.searchText,
  limit: PropertiesConstants.PROPERTIES_LIMIT_DEFAULT,
  startKeyExclusive: PropertiesConstants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
});
